import type { AccountDto, DriverDto, VehicleDto } from '../api/adminServices'

export type EntityLookups = {
  accountById: Map<string, AccountDto>
  driverById: Map<string, DriverDto>
  vehicleById: Map<string, VehicleDto>
}

const ACCOUNT_ID_KEYS = new Set([
  'shipperaccountid',
  'freighteraccountid',
  'accountid',
  'shipper_account_id',
  'freighter_account_id',
  'account_id',
])

const DRIVER_ID_KEYS = new Set(['driverid', 'driver_id'])

const VEHICLE_ID_KEYS = new Set(['vehicleid', 'vehicle_id'])

export function accountDisplayName(
  accountId: string | undefined | null,
  accountById: Map<string, AccountDto>
): string {
  if (!accountId) return '—'
  const account = accountById.get(accountId)
  return account?.businessName || account?.name || `${accountId.substring(0, 10)}…`
}

export function driverDisplayName(
  driverId: string | undefined | null,
  driverById: Map<string, DriverDto>
): string {
  if (!driverId) return '—'
  const driver = driverById.get(driverId)
  return driver?.user?.name || driver?.user?.nickName || driver?.user?.phone || `${driverId.substring(0, 10)}…`
}

export function vehiclePlateDisplay(
  vehicleId: string | undefined | null,
  vehicleById: Map<string, VehicleDto>
): string {
  if (!vehicleId) return '—'
  const vehicle = vehicleById.get(vehicleId)
  return vehicle?.plateNo || `${vehicleId.substring(0, 10)}…`
}

export function isAccountIdColumn(key: string): boolean {
  return ACCOUNT_ID_KEYS.has(key.toLowerCase())
}

export function isDriverIdColumn(key: string): boolean {
  return DRIVER_ID_KEYS.has(key.toLowerCase())
}

export function isVehicleIdColumn(key: string): boolean {
  return VEHICLE_ID_KEYS.has(key.toLowerCase())
}

export function formatColumnHeader(key: string): string {
  const lower = key.toLowerCase()
  if (lower === 'driverid' || lower === 'driver_id') return 'Driver'
  if (lower === 'vehicleid' || lower === 'vehicle_id') return 'Vehicle'
  if (lower === 'shipperaccountid' || lower === 'shipper_account_id') return 'Shipper'
  if (lower === 'freighteraccountid' || lower === 'freighter_account_id') return 'Freighter'
  if (lower === 'accountid' || lower === 'account_id') return 'Account'
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim()
}

export function resolveEntityCellValue(
  key: string,
  value: unknown,
  lookups: EntityLookups
): { display: string; tooltip?: string; accountId?: string } | null {
  if (value === null || value === undefined || value === '' || value === 'null') return null
  if (typeof value !== 'string' && typeof value !== 'number') return null
  const id = String(value)
  if (!id) return null

  if (isDriverIdColumn(key)) {
    return { display: driverDisplayName(id, lookups.driverById), tooltip: id }
  }
  if (isVehicleIdColumn(key)) {
    return { display: vehiclePlateDisplay(id, lookups.vehicleById), tooltip: id }
  }
  if (isAccountIdColumn(key)) {
    return {
      display: accountDisplayName(id, lookups.accountById),
      tooltip: id,
      accountId: id,
    }
  }
  return null
}

export function normalizePagedData<T>(payload: unknown): T[] {
  if (Array.isArray(payload)) return payload as T[]
  if (!payload || typeof payload !== 'object') return []
  const maybe = payload as { content?: unknown; data?: unknown }
  if (Array.isArray(maybe.content)) return maybe.content as T[]
  if (Array.isArray(maybe.data)) return maybe.data as T[]
  return []
}

export function buildEntityLookups(
  accountsPayload: unknown,
  driversPayload: unknown,
  vehiclesPayload: unknown
): EntityLookups {
  const accounts = normalizePagedData<AccountDto>(accountsPayload)
  const drivers = normalizePagedData<DriverDto>(driversPayload)
  const vehicles = normalizePagedData<VehicleDto>(vehiclesPayload)

  const accountById = new Map<string, AccountDto>()
  accounts.forEach((a) => {
    if (a?.id) accountById.set(a.id, a)
  })

  const driverById = new Map<string, DriverDto>()
  drivers.forEach((d) => {
    if (d?.id) driverById.set(d.id, d)
  })

  const vehicleById = new Map<string, VehicleDto>()
  vehicles.forEach((v) => {
    if (v?.id) vehicleById.set(v.id, v)
  })

  return { accountById, driverById, vehicleById }
}
